import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Save, Loader2, Upload, X, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Organization } from '../../services/organization.service';
import organizationService from '../../services/organization.service';
import { useAppDispatch } from '../../redux/store';
import { setCurrentOrganization } from '../../redux/slices/organizationSlice';
import { canManageOrganization } from '../../utils/organizationPermissions';

interface OrganizationInfoProps {
    organization: Organization;
    currentUser: any;
    onUpdate: () => void;
}

/**
 * Organization Info Component
 * Displays and allows editing of organization details (admin only)
 */
const OrganizationInfo: React.FC<OrganizationInfoProps> = ({ organization, currentUser, onUpdate }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        name: organization.name,
        customSlug: organization.customSlug || organization.slug
    });

    // Logo upload state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [logoError, setLogoError] = useState<string | null>(null);
    const [logoToDelete, setLogoToDelete] = useState(false); // Track if user wants to delete logo

    // Sync form data when organization prop changes
    useEffect(() => {
        console.log('🟢 Organization prop changed:', organization);
        setFormData({
            name: organization.name,
            customSlug: organization.customSlug || organization.slug
        });
        // Reset logo state when organization changes
        setSelectedFile(null);
        setLogoPreview(null);
        setLogoToDelete(false);
    }, [organization.name, organization.customSlug, organization.slug, organization.logoUrl]);

    const canEdit = canManageOrganization(currentUser);

    const handleSave = async () => {
        try {
            setIsSaving(true);
            setError(null);
            setSuccess(false);

            // First, update organization name and slug
            let updated = await organizationService.updateOrganization({
                name: formData.name,
                customSlug: formData.customSlug
            });

            // Then handle logo upload if a new file was selected
            if (selectedFile) {
                console.log('🔵 Uploading logo file:', selectedFile.name);
                updated = await organizationService.uploadLogo(selectedFile);
                console.log('🔵 Logo upload response:', updated);
            }

            // Or handle logo deletion if requested
            if (logoToDelete && organization.logoUrl) {
                console.log('🔵 Deleting logo');
                updated = await organizationService.deleteLogo();
                console.log('🔵 Logo delete response:', updated);
            }

            console.log('🔵 Updating Redux with organization:', updated);
            dispatch(setCurrentOrganization(updated));
            setSuccess(true);
            setIsEditing(false);
            setSelectedFile(null);
            setLogoPreview(null);
            setLogoToDelete(false);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccess(false), 3000);

            onUpdate();
        } catch (err: any) {
            console.error('Failed to update organization:', err);
            setError(err.response?.data?.error || err.message || t('errors.organization.failedToLoad', 'Failed to load organization data'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setFormData({
            name: organization.name,
            customSlug: organization.customSlug || organization.slug
        });
        setIsEditing(false);
        setError(null);
        setSelectedFile(null);
        setLogoPreview(null);
        setLogoError(null);
        setLogoToDelete(false);
    };

    // Validate slug format
    const isValidSlug = (slug: string) => {
        return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
    };

    // Logo upload handlers
    const validateLogoFile = (file: File): string | null => {
        const allowedTypes = ['image/svg+xml', 'image/png', 'image/webp', 'image/jpeg', 'image/jpg'];
        const maxSize = 500 * 1024; // 500KB

        if (!allowedTypes.includes(file.type)) {
            return 'Invalid file type. Please upload SVG, PNG, WebP, or JPEG.';
        }

        if (file.size > maxSize) {
            return `File too large. Maximum size is ${maxSize / 1024}KB.`;
        }

        return null;
    };

    const handleFileSelect = (file: File) => {
        const error = validateLogoFile(file);
        if (error) {
            setLogoError(error);
            setSelectedFile(null);
            setLogoPreview(null);
            return;
        }

        setLogoError(null);
        setSelectedFile(file);
        setLogoToDelete(false); // Clear delete flag if selecting new file

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const hasChanges = formData.name !== organization.name ||
        formData.customSlug !== (organization.customSlug || organization.slug) ||
        selectedFile !== null ||
        logoToDelete;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    {t('organization.settings.profileInformation')}
                </CardTitle>
                <CardDescription>
                    {canEdit
                        ? t('organization.settings.description')
                        : t('organization.settings.adminOnly')
                    }
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {success && (
                    <Alert className="border-green-500 bg-green-50 text-green-900">
                        <AlertDescription>{t('organization.settings.saved')}</AlertDescription>
                    </Alert>
                )}

                {/* Organization Name */}
                <div className="space-y-2">
                    <Label htmlFor="org-name">{t('organization.settings.organizationName')}</Label>
                    <Input
                        id="org-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        disabled={!isEditing || !canEdit}
                        placeholder={t('organization.settings.namePlaceholder')}
                    />
                </div>

                {/* Custom Slug (Editable) */}
                <div className="space-y-2">
                    <Label htmlFor="custom-slug">{t('organization.settings.customUrlSlug')}</Label>
                    <Input
                        id="custom-slug"
                        value={formData.customSlug}
                        onChange={(e) => setFormData({ ...formData, customSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                        disabled={!isEditing || !canEdit}
                        placeholder={t('organization.settings.slugPlaceholder')}
                        className={!isValidSlug(formData.customSlug) && isEditing ? 'border-red-500' : ''}
                    />
                    <p className="text-xs text-muted-foreground">
                        Your URL: <span className="font-mono">app.omnilaw.ai/{formData.customSlug}</span>
                    </p>
                    {!isValidSlug(formData.customSlug) && isEditing && (
                        <p className="text-xs text-red-500">
                            Slug must contain only lowercase letters, numbers, and hyphens
                        </p>
                    )}
                </div>

                {/* Organization Logo */}
                <div className="space-y-2 pt-4 border-t">
                    <Label htmlFor="org-logo">{t('organization.settings.organizationLogo')}</Label>
                    <p className="text-xs text-muted-foreground">
                        Upload your company logo (SVG, PNG, WebP, or JPEG • Max 500KB)
                    </p>

                    {logoError && (
                        <Alert variant="destructive">
                            <AlertDescription>{logoError}</AlertDescription>
                        </Alert>
                    )}

                    {/* Current Logo or Upload Area */}
                    <div className="space-y-3">
                        {/* Show current logo when not editing or no changes */}
                        {organization.logoUrl && !isEditing && !selectedFile && !logoToDelete && (
                            <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
                                <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-background border rounded-lg overflow-hidden">
                                    <img
                                        src={organization.logoUrl}
                                        alt={t('organization.settings.organizationLogo')}
                                        className="max-w-full max-h-full object-contain"
                                    />
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium">{t('organization.settings.currentLogo')}</p>
                                    <p className="text-xs text-muted-foreground">{t('organization.settings.uploadedLogo')}</p>
                                </div>
                            </div>
                        )}

                        {/* Upload/Edit Area - only show when editing */}
                        {canEdit && isEditing && (
                            <div className="space-y-3">
                                {/* Show current logo with delete option when editing */}
                                {organization.logoUrl && !selectedFile && !logoToDelete && (
                                    <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
                                        <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-background border rounded-lg overflow-hidden">
                                            <img
                                                src={organization.logoUrl}
                                                alt={t('organization.settings.organizationLogo')}
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{t('organization.settings.currentLogo')}</p>
                                            <p className="text-xs text-muted-foreground">{t('organization.settings.clickToChange')}</p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setLogoToDelete(true);
                                                setSelectedFile(null);
                                                setLogoPreview(null);
                                            }}
                                        >
                                            <X className="h-4 w-4 mr-1" />
                                            Remove
                                        </Button>
                                    </div>
                                )}

                                {/* Show "Logo will be removed" message */}
                                {logoToDelete && !selectedFile && (
                                    <div className="flex items-center gap-4 p-4 border border-destructive/50 rounded-lg bg-destructive/5">
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-destructive">{t('organization.settings.logoRemoved')}</p>
                                            <p className="text-xs text-muted-foreground">{t('organization.settings.saveToConfirm')}</p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setLogoToDelete(false)}
                                        >
                                            Undo
                                        </Button>
                                    </div>
                                )}

                                <input
                                    type="file"
                                    id="logo-upload"
                                    accept="image/svg+xml,image/png,image/webp,image/jpeg,image/jpg"
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                    disabled={!isEditing}
                                />

                                {/* Show dropzone only when no file is selected and not deleting */}
                                {!selectedFile && !logoPreview && !logoToDelete && (
                                    <div
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`
                                            border-2 border-dashed rounded-lg p-6 text-center cursor-pointer
                                            transition-colors
                                            ${isDragging ? 'border-gold bg-gold/5' : 'border-muted-foreground/25 hover:border-gold/50'}
                                        `}
                                        onClick={() => document.getElementById('logo-upload')?.click()}
                                    >
                                        <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                        <p className="text-sm font-medium mb-1">
                                            {isDragging ? t('errors.organization.dropLogo', 'Drop logo here') : organization.logoUrl ? t('errors.organization.changeLogo', 'Change logo') : t('errors.organization.uploadLogo', 'Click to upload or drag and drop')}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            SVG, PNG, WebP or JPEG (max 500KB)
                                        </p>
                                    </div>
                                )}

                                {/* Show selected file preview with clear button */}
                                {selectedFile && logoPreview && (
                                    <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/50">
                                        <div className="flex-shrink-0 w-20 h-20 flex items-center justify-center bg-background border rounded-lg overflow-hidden">
                                            <img
                                                src={logoPreview}
                                                alt={t('organization.settings.selectedLogoPreview')}
                                                className="max-w-full max-h-full object-contain"
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-medium">{t('organization.settings.newLogoSelected')}</p>
                                            <p className="text-xs text-muted-foreground">{selectedFile.name}</p>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setLogoPreview(null);
                                                setLogoError(null);
                                            }}
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {!canEdit && !organization.logoUrl && (
                            <p className="text-sm text-muted-foreground italic">
                                No logo uploaded
                            </p>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                {canEdit && (
                    <div className="flex gap-2 pt-4">
                        {!isEditing ? (
                            <Button onClick={() => setIsEditing(true)}>
                                {t('common.buttons.edit')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving || !hasChanges || !isValidSlug(formData.customSlug)}
                                >
                                    {isSaving ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            {t('organization.settings.saving')}
                                        </>
                                    ) : (
                                        <>
                                            <Save className="mr-2 h-4 w-4" />
                                            {t('organization.settings.saveChanges')}
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                >
                                    {t('common.buttons.cancel')}
                                </Button>
                            </>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default OrganizationInfo;
